import ChatScheduledMessages from '@/common/views/whatsapp-view/ChatScheduledMessages';
import RemindersSidebar from '@/common/views/whatsapp-view/RemindersSidebar';
import QuickAgentsSidebar from '@/common/views/whatsapp-view/QuickAgentsSidebar';
import AiAssistant from '@common-components/ai-assistant/ai-assistant';
import BTDrawer from "@common-components/bt-drawer/bt-drawer";
import { BANNER_OFFSET_PAPER_SX } from "@common-components/dunning-banner/banner-offset";
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { ALL_SIDEBAR_COMPONENTS, btLayoutSidebarActions, ComponentToShow } from "@common-reducers/BtLayoutSidebarReducer";
import { theme } from '@common/theme';
import { useMediaQuery } from "@mui/material";
import React, { ReactNode, useCallback, useEffect } from 'react';
import TasksSideBarWrapper from './tasks-sidebar-wrapper';
import NotesPanel from '../../../extension/context/content/components/notes-sidebar/notes-panel';
import { ExtensionImageProxyProvider } from '@/extension/context/content/components/ExtensionImageProxyProvider';

interface BtLayoutSidebarProps {
  showToggle?: boolean;
  toggleIcon?: ReactNode;
  excludedComponents?: ComponentToShow[];
}

/**
 * BtLayoutSidebar component that displays different components in a simple drawer
 * Uses Redux for state management to control drawer open/close state and component selection
 */
const BtLayoutSidebar: React.FC<BtLayoutSidebarProps> = ({ showToggle, toggleIcon, excludedComponents = [] }) => {
  const dispatch = useAppDispatch();
  const { isOpen, componentToShow } = useAppSelector(
    (state) => state.BtLayoutSidebarReducer
  );
  const direction = useAppSelector((state) => state.UserReducer.userWhatsappDirection);
  const isDesktop = useMediaQuery(theme.breakpoints.up(786));

  const handleClose = useCallback(() => {
    dispatch(btLayoutSidebarActions.closeSidebar());
  }, [dispatch]);

  const handleToggle = () => {
    dispatch(btLayoutSidebarActions.toggleSidebar());
  };

  // Handle Escape key to close drawer
  useEffect(() => {
    const handleEscapeKeyClose = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscapeKeyClose);
    return () => {
      document.removeEventListener("keydown", handleEscapeKeyClose);
    };
  }, [handleClose, isOpen]);



  // Render the appropriate component based on componentToShow state
  const renderComponent = () => {
    // If the component is excluded, return null or handle accordingly
    // Note: The drawer itself might still be open if we don't also check this in shouldOpen
    if (excludedComponents.includes(componentToShow)) {
      return null;
    }

    switch (componentToShow) {
      case 'ChatScheduledMessages':
        return <ChatScheduledMessages />;
      case 'btAiAssistant':
        return <AiAssistant />;
      case 'Reminders':
        return <RemindersSidebar />;
      case 'QuickAgents':
        return <QuickAgentsSidebar />;
      case 'Notes':
        return (
          <ExtensionImageProxyProvider>
            <NotesPanel />
          </ExtensionImageProxyProvider>
        );
      case 'Tasks':
        return <TasksSideBarWrapper />;
      default:
        return <ChatScheduledMessages />;
    }
  };

  // Filter out excluded components from supported list for opening check
  const activeSupportedComponents = ALL_SIDEBAR_COMPONENTS.filter((component) => !excludedComponents.includes(component));

  const shouldOpen = isOpen && activeSupportedComponents.includes(componentToShow);

  const isWidePanel = componentToShow === 'Tasks' || componentToShow === 'Notes';
  const drawerWidth = isWidePanel ? "700px" : "500px";

  return (
    <BTDrawer
      open={shouldOpen}
      onClose={handleClose}
      variant={isDesktop ? "persistent" : "temporary"}
      anchor={direction === "rtl" ? "left" : "right"}
      showToggle={showToggle}
      onToggle={handleToggle}
      toggleIcon={toggleIcon}
      PaperProps={{
        sx: {
          width: drawerWidth,
          // MUI's paper is position:fixed; top:0, so it sat under the dunning banner (#852).
          ...BANNER_OFFSET_PAPER_SX,
        },
      }}
    >
      {renderComponent()}
    </BTDrawer>
  );
};

export default BtLayoutSidebar;
